import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import Section from "./Section";
import {
  getFoamSettings,
  patchFoamSettings,
  type FoamSettings,
} from "../../../api/foamSettings";

/**
 * The bands behind the foam diagram's colors.
 *
 * Every field may be left blank. Blank is not zero and not a default: it means
 * nobody has said what normal is for that band, and the diagram draws it grey
 * instead of guessing a color. Filling one in is what lights it up.
 */

type FieldKey = keyof Omit<FoamSettings, "updated_at">;

type FieldDef = {
  key: FieldKey;
  label: string;
  unit: string;
  help?: string;
};

type GroupDef = {
  title: string;
  caption: string;
  fields: FieldDef[];
};

const GROUPS: GroupDef[] = [
  {
    title: "Butane — high pressure side",
    caption:
      "Inside the running band is green. Outside it is orange; above the safety limit is red.",
    fields: [
      { key: "hp_running_min", label: "Running min", unit: "PSI" },
      { key: "hp_running_max", label: "Running max", unit: "PSI" },
      { key: "hp_safety_max", label: "Safety max", unit: "PSI" },
    ],
  },
  {
    title: "Butane — low pressure side",
    caption: "Inside the running band is green. Anywhere outside it is red.",
    fields: [
      { key: "lp_running_min", label: "Running min", unit: "PSI" },
      { key: "lp_running_max", label: "Running max", unit: "PSI" },
    ],
  },
  {
    title: "Cooling water",
    caption: "Inside the band is green. Outside it is orange — there is no red.",
    fields: [
      { key: "temp_min", label: "Temp min", unit: "°C" },
      { key: "temp_max", label: "Temp max", unit: "°C" },
    ],
  },
];

const ALL_FIELDS = GROUPS.flatMap((g) => g.fields);

/** Pairs that only make sense in order, checked when both ends are filled in. */
const ORDERED: [FieldKey, FieldKey, string][] = [
  ["hp_running_min", "hp_running_max", "High pressure running min must be ≤ max"],
  ["hp_running_max", "hp_safety_max", "High pressure safety max must be ≥ running max"],
  ["lp_running_min", "lp_running_max", "Low pressure running min must be ≤ max"],
  ["temp_min", "temp_max", "Cooling water temp min must be ≤ max"],
];

const toInput = (v: number | null | undefined) =>
  typeof v === "number" && Number.isFinite(v) ? String(v) : "";

type Draft = Record<FieldKey, string>;

const draftFrom = (s: FoamSettings): Draft =>
  ALL_FIELDS.reduce((acc, f) => {
    acc[f.key] = toInput(s[f.key]);
    return acc;
  }, {} as Draft);

const FoamSettingsSection: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orig, setOrig] = useState<FoamSettings | null>(null);
  const [draft, setDraft] = useState<Draft>(() =>
    ALL_FIELDS.reduce((acc, f) => {
      acc[f.key] = "";
      return acc;
    }, {} as Draft)
  );

  const [snack, setSnack] = useState<{
    open: boolean;
    msg: string;
    severity: "success" | "error";
  }>({ open: false, msg: "", severity: "success" });

  const showSnack = (msg: string, severity: "success" | "error") =>
    setSnack({ open: true, msg, severity });

  useEffect(() => {
    const controller = new AbortController();
    getFoamSettings(controller.signal)
      .then((j) => {
        setOrig(j);
        setDraft(draftFrom(j));
      })
      .catch((e: any) => {
        if (e?.name !== "CanceledError")
          showSnack("Failed to load foam thresholds", "error");
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, []);

  const set = (key: FieldKey, value: string) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  const validationError = useMemo(() => {
    for (const f of ALL_FIELDS) {
      const raw = draft[f.key].trim();
      if (raw !== "" && !Number.isFinite(Number(raw)))
        return `${f.label} must be a number, or blank`;
    }
    for (const [lo, hi, msg] of ORDERED) {
      const a = draft[lo].trim();
      const b = draft[hi].trim();
      if (a !== "" && b !== "" && Number(a) > Number(b)) return msg;
    }
    return null;
  }, [draft]);

  // Only send what moved, so an untouched blank stays blank rather than being
  // re-sent as an explicit clear.
  const changes = useMemo(() => {
    if (!orig) return {};
    const out: Partial<Record<FieldKey, number | null>> = {};
    for (const f of ALL_FIELDS) {
      const raw = draft[f.key].trim();
      const next = raw === "" ? null : Number(raw);
      if (next !== (orig[f.key] ?? null)) out[f.key] = next;
    }
    return out;
  }, [orig, draft]);

  const dirty = Object.keys(changes).length > 0;

  const save = async () => {
    if (validationError) {
      showSnack(validationError, "error");
      return;
    }
    setSaving(true);
    try {
      const j = await patchFoamSettings(changes);
      setOrig(j);
      setDraft(draftFrom(j));
      showSnack("Foam thresholds saved", "success");
    } catch (e: any) {
      showSnack(
        e?.response?.data?.detail || e?.message || "Save failed",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  const unset = ALL_FIELDS.filter((f) => draft[f.key].trim() === "").length;

  return (
    <Section
      title="Foam Line Thresholds"
      subtitle="The bands the Foam diagram colors against. Leave a field blank and that part of the diagram stays grey rather than guessing."
      rightAction={
        <Button
          variant="contained"
          size="small"
          onClick={save}
          disabled={loading || saving || !dirty || !!validationError}
        >
          {saving ? <CircularProgress size={18} /> : "Save"}
        </Button>
      }
    >
      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <>
          {unset > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {unset === ALL_FIELDS.length
                ? "No thresholds set yet — the butane and cooling water parts of the Foam diagram are grey until these are filled in."
                : `${unset} threshold${unset === 1 ? "" : "s"} still unset; those bands stay grey on the diagram.`}
            </Alert>
          )}

          {GROUPS.map((g) => (
            <Box key={g.title} sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {g.title}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", mb: 1.5 }}
              >
                {g.caption}
              </Typography>
              <Grid container spacing={2}>
                {g.fields.map((f) => (
                  <Grid item xs={12} sm={4} key={f.key}>
                    <TextField
                      fullWidth
                      size="small"
                      label={f.label}
                      value={draft[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                      placeholder="Not set"
                      inputProps={{ inputMode: "decimal" }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            {f.unit}
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}

          {validationError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {validationError}
            </Alert>
          )}

          {orig?.updated_at && (
            <Typography variant="caption" color="text.secondary">
              Last updated {new Date(orig.updated_at).toLocaleString()}
            </Typography>
          )}
        </>
      )}

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </Section>
  );
};

export default FoamSettingsSection;
